import { IconButton, Typography } from '@mui/material';
import { RotateCcw } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Tooltip } from 'src/components';
import { ModalContainer, ModalList, ModalListItem } from 'src/components/ModalContainer';
import { SelectTrigger } from 'src/components/SelectTrigger';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useAddresses } from 'src/graphql/entities/address';
import { useSearchAddresses } from 'src/graphql/entities/search/search-address';
import { ConditionInput } from 'src/graphql/types';
import {
  excludedAddressIdsFromPreference,
  MAX_WEEKLY_DIGEST_EXCLUDED_SITES,
  skippedSitesLabel,
} from 'src/pages/Settings/notifications/utils';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

const SKIPPED_SITES_FOLDER_ID = 'weekly-digest-skipped-sites';

const ADDRESS_QUERY_CONTEXT = { omitIsolationModeHeader: true };

export type WeeklyDigestSiteExclusionsProps = {
  emailEnabled: boolean;
  partnerId?: number;
  config?: string | null;
  onChange: (addressIds: string[]) => void | Promise<void>;
};

export const WeeklyDigestSiteExclusions = ({
  emailEnabled,
  partnerId,
  config,
  onChange,
}: WeeklyDigestSiteExclusionsProps) => {
  const { showErrorAlert } = useNotifications();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const sitePartnerId = partnerForSiteSearch(selectedPartnerId, partnerId);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [skippedExpanded, setSkippedExpanded] = useState(true);
  const [localIds, setLocalIds] = useState<string[] | null>(null);
  const savedIds = excludedAddressIdsFromPreference(config);
  const pendingRef = useRef<string[] | null>(null);
  const sendingRef = useRef(false);
  const shownIds = localIds ?? savedIds;
  const shownIdsKey = shownIds.join(',');

  if (
    localIds &&
    !pendingRef.current &&
    !sendingRef.current &&
    sameAddressIds(localIds, savedIds)
  ) {
    setLocalIds(null);
  }

  const partnerFilter = useMemo<ConditionInput[]>(
    () =>
      sitePartnerId === undefined
        ? []
        : [{ attribute: 'partnerId', operator: '=', value: String(sitePartnerId) }],
    [sitePartnerId],
  );

  const skippedFilters = useMemo<ConditionInput[]>(
    () =>
      shownIdsKey && sitePartnerId !== undefined
        ? [
            { attribute: 'addressId', operator: 'in', value: shownIdsKey },
            { attribute: 'partnerId', operator: '=', value: String(sitePartnerId) },
          ]
        : [],
    [shownIdsKey, sitePartnerId],
  );

  const {
    records: searchResults,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
  } = useSearchAddresses({
    searchTerm,
    filter: partnerFilter,
    limit: 20,
    showResultsWhenEmptySearchTerm: true,
    skip: !open || sitePartnerId === undefined,
    context: ADDRESS_QUERY_CONTEXT,
  });

  const { addresses: skippedAddresses, loading: skippedLoading } = useAddresses(
    {
      filters: skippedFilters,
      limit: shownIds.length || 1,
      includeDeleted: true,
    },
    {
      skip: !open || shownIds.length === 0 || sitePartnerId === undefined,
      context: ADDRESS_QUERY_CONTEXT,
    },
  );

  const skippedById = useMemo(() => {
    const addresses = new Map<
      string,
      { fullAddress?: string | null; address1?: string | null }
    >();
    for (const address of skippedAddresses ?? []) {
      addresses.set(address.addressId, address);
    }
    for (const address of searchResults ?? []) {
      if (shownIds.includes(address.addressId)) {
        addresses.set(address.addressId, address);
      }
    }
    return addresses;
  }, [skippedAddresses, searchResults, shownIds]);

  const close = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const saveExcludedSites = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      while (pendingRef.current) {
        const ids = pendingRef.current;
        pendingRef.current = null;
        await onChange(ids);
      }
    } catch {
      if (!pendingRef.current) setLocalIds(null);
    } finally {
      sendingRef.current = false;
      if (pendingRef.current) void saveExcludedSites();
    }
  };

  const queueExcludedSites = (ids: string[]) => {
    pendingRef.current = ids;
    setLocalIds(ids);
    void saveExcludedSites();
  };

  const toggleExcludedSite = (item: ModalListItem) => {
    if (item.id === SKIPPED_SITES_FOLDER_ID) return;
    const current = pendingRef.current ?? localIds ?? savedIds;
    const next = current.includes(item.id)
      ? current.filter((id) => id !== item.id)
      : [...current, item.id];
    if (next.length > MAX_WEEKLY_DIGEST_EXCLUDED_SITES) {
      showErrorAlert(`You can skip up to ${MAX_WEEKLY_DIGEST_EXCLUDED_SITES} sites`);
      return;
    }
    queueExcludedSites(next);
  };

  const clearSkippedSites = () => {
    queueExcludedSites([]);
  };

  const items = skippedSiteItems({
    shownIds,
    expanded: skippedExpanded,
    skippedById,
    searchResults: searchResults ?? [],
    onClear: clearSkippedSites,
  });

  if (!emailEnabled) {
    return (
      <Typography variant="body2">
        {savedIds.length ? skippedSitesLabel(savedIds.length) : 'All sites'}
      </Typography>
    );
  }

  return (
    <>
      <SelectTrigger
        label={shownIds.length ? skippedSitesLabel(shownIds.length) : 'All sites'}
        aria-label="Skip sites"
        onClick={() => setOpen(true)}
        sx={{ width: '100%' }}
      />
      <ModalContainer open={open} size="medium" title="Skip sites" onClose={close}>
        <ModalList
          items={items}
          onSelect={toggleExcludedSite}
          onExpandedChange={(_item, expanded) => setSkippedExpanded(expanded)}
          shouldFilter={false}
          onSearchChange={setSearchTerm}
          loading={isLoading || skippedLoading}
          placeholder="Search sites"
          emptyText="No sites found"
          loadMore={
            hasMore && (searchResults?.length ?? 0) > 0
              ? { onSelect: () => void loadMore(), loading: isLoadingMore }
              : undefined
          }
          testId="weekly-digest-skipped-sites"
        />
      </ModalContainer>
    </>
  );
};

function partnerForSiteSearch(
  selectedPartnerId: number | null,
  preferencePartnerId?: number,
): number | undefined {
  if (selectedPartnerId !== null && selectedPartnerId !== PLATFORM_PARTNER_ID) {
    return selectedPartnerId;
  }
  return preferencePartnerId;
}

function skippedSiteItems(input: {
  shownIds: string[];
  expanded: boolean;
  skippedById: Map<string, { fullAddress?: string | null; address1?: string | null }>;
  searchResults: {
    addressId: string;
    fullAddress?: string | null;
    address1?: string | null;
  }[];
  onClear: () => void;
}): ModalListItem[] {
  const skipped = new Set(input.shownIds);
  const folder: ModalListItem[] = input.shownIds.length
    ? [
        {
          id: SKIPPED_SITES_FOLDER_ID,
          title: skippedSitesLabel(input.shownIds.length),
          expanded: input.expanded,
          actions: (
            <Tooltip title="Clear skipped">
              <span>
                <IconButton
                  size="small"
                  aria-label="Clear skipped"
                  onClick={input.onClear}
                  data-testid="weekly-digest-clear-skipped"
                  sx={{ '& svg': { width: '0.875rem', height: '0.875rem' } }}
                >
                  <RotateCcw />
                </IconButton>
              </span>
            </Tooltip>
          ),
        },
        ...(input.expanded
          ? input.shownIds.map((id) => ({
              id,
              title: siteLabel(input.skippedById.get(id)),
              level: 1,
              checked: true,
            }))
          : []),
      ]
    : [];

  const results: ModalListItem[] = input.searchResults
    .filter((address) => !skipped.has(address.addressId))
    .map((address) => ({
      id: address.addressId,
      title: siteLabel(address),
      checked: false,
    }));

  return [...folder, ...results];
}

function siteLabel(address?: {
  fullAddress?: string | null;
  address1?: string | null;
}): string {
  return address?.fullAddress || address?.address1 || 'Site';
}

function sameAddressIds(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const ids = new Set(right);
  return left.every((id) => ids.has(id));
}
